/**
 * Logo Scan2Eat.
 *
 * Le symbole -- assiette, couverts, feuille et ligne de scan -- vit dans
 * `public/logo-symbole.svg`, extrait du logo complet. Il est servi en image
 * plutot qu'inline : le fichier pese onze kilo-octets, que le navigateur met
 * en cache une fois pour toutes au lieu de les repeter dans chaque page.
 *
 * Le nom reste du **texte**, a cote du symbole. Le logo complet le porte aussi,
 * mais il l'ecrit sous le symbole, en carre : inutilisable dans une barre
 * laterale ou un en-tete, et illisible en petit. Ecrit en texte, il s'adapte au
 * theme, se lit par un lecteur d'ecran et se cherche dans la page.
 *
 * Le logo complet reste disponible dans `public/logo.svg`, pour les usages ou
 * la place ne manque pas.
 *
 * Composant identique dans web-client, web-admin et web-platform.
 */

interface LogoProps {
  /** Cote du symbole en px. Defaut : 36, minimum : 24. */
  size?: number;
  /** Affiche le nom a droite du symbole (logo horizontal). */
  withWordmark?: boolean;
  /** Fond sur lequel il se pose : decide de la couleur du nom. */
  tone?: 'light' | 'dark';
  className?: string;
}

const WORDMARK = 'Scan2Eat';

export function Logo({ size = 36, withWordmark = false, tone = 'light', className }: LogoProps) {
  const side = Math.max(24, size);
  const symbol = (
    <img
      src="/logo-symbole.svg"
      width={side}
      height={side}
      alt={withWordmark ? '' : WORDMARK}
      aria-hidden={withWordmark ? true : undefined}
      // Le fichier est un carre a angles vifs ; l'arrondi est pose ici pour
      // s'accorder aux cartes et aux boutons de l'interface.
      style={{ flexShrink: 0, display: 'block', borderRadius: Math.round(side * 0.22) }}
    />
  );

  if (!withWordmark) {
    return <span className={className} style={{ display: 'inline-flex' }}>{symbol}</span>;
  }

  return (
    <span
      className={className}
      role="img"
      aria-label={WORDMARK}
      style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(side * 0.3) }}
    >
      {symbol}
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: Math.round(side * 0.5),
          letterSpacing: '-0.02em',
          lineHeight: 1,
          color: tone === 'dark' ? 'var(--brand-creme)' : 'var(--color-text)',
          userSelect: 'none',
        }}
      >
        {WORDMARK}
      </span>
    </span>
  );
}
